import { ARProviderProps } from "@animech/ar";
import {
	InputValidity,
	InputValuesPortalProps,
	ModalManager,
	Notification,
	NotificationManager,
} from "@animech/ui";
import { Theme } from "@emotion/react";
import { FunctionComponent, ReactElement, ReactNode } from "react";
import { render } from "react-dom";
import { Entity } from "playcanvas";
import { InvalidTemplatePropertyAccessError } from "@animech/axiom-configurator";
import {
	ConfiguratorController,
	FieldComponentBuilder,
	FieldValueMeta,
	UiBuilder as UiBuilderClass,
	UiNodeQuerier,
} from "@animech/axiom-ui-builder";
import {
	getLocalizablePropertyValue,
	resolveProperty,
} from "@animech/axiom-utility";
import { QueryClientProvider } from "@tanstack/react-query";
import {
	Locale,
	ProductId,
} from "@animech/axiom-format/src/definitions/definition-bindings";
import {
	App,
	ARModal,
	ARModalProps,
	CartModal,
	FieldInfoModal,
	FieldValueInfoModal,
	FieldValuesModal,
	LanguageModal,
	RestartModal,
	screenStackType,
	SendEmailModal,
} from "./components";
import {
	LogoData,
	UiData,
	UiDataProvider,
	UiLayout,
	UiSlot,
	UiView,
	uiViews,
} from "./data";
import {
	CustomComponentBuilderRouter,
	CustomProductListComponentBuilder,
	ColorPickerComponentBuilder,
	AccordionComponentBuilder,
	AccordionGroupComponentBuilder,
} from "./builders";
import { ToggleButtonGroupComponentBuilder } from "./builders/toggle-button-group-component-builder";
import {
	DevToolsData,
	DevToolsProvider,
} from "./components/panels/developer-panel/dev-tools-context";
import {
	Scene,
	ConfiguratorBridge,
	SettingsFromQuery,
	SceneObjectData,
	UnitSystem,
} from "src/core";
import {
	assertIsLocalizable,
	BuildInfo,
	ConfigurationHistoryManager,
	Configurator,
} from "src/utility";
import {
	Property,
	PropertyValue,
	UiNode,
	AnyWallEditingToolType,
	ActionContext,
	Localizer,
	UiBuilderCallbacks,
	UiBuilder,
	MetaRegistry,
	SceneObject,
} from "src/common";
import {
	ConfigurationMode,
	CameraMode,
	WallSnapSettings,
	WallVisualizationSettings,
} from "src/scene";
import {
	defaultRoomAppearance,
	FloorStyleName,
	RoomAppearance,
	WallColorName,
} from "src/scene/room";
import { ApiDataController, ApiDataManager, queryClient } from "src/api";
import { PostMessageHandler } from "src/postmessage-handler";
import { ColorSelectWithColorPropagationComponentBuilder } from "src/ui/builders/color-select-with-color-propagation-component-builder";
import { Product } from "src/definitions/type-definitions";
import { root } from "@definitions";
import { isProductThemeInStock } from "src/core/articles/stock-check";
import { DecimalInputComponentBuilder } from "src/ui/builders/decimal-input-component-builder";
import { ThumbnailDropDownComponentBuilder } from "src/ui/builders/thumbnail-dropdown-component-builder";
import { ConditionalMessageComponentBuilder } from "src/ui/builders/conditional-message-component-builder";
import { HorizontalCardSelectComponentBuilder } from "src/ui/builders/horizontal-card-select-component-builder";
import { VisibleGroupComponentBuilder } from "src/ui/builders/visible-group-component-builder";
export type UiManagerDefinitions = {
	slotNodes: UiSlotNodes;
	logo: LogoData;
	layout: UiLayout;
	initialActiveProperty: Property;
};

export type UiSlotNodes = { [key in UiSlot]?: UiNode };

export type UiManagerArgs = {
	configurator: Configurator;
	configuratorBridge: ConfiguratorBridge;
	domRoot: string;
	resourceBaseUrl: string;
	theme: Theme;
	canvas: HTMLCanvasElement;
	metaRegistry: MetaRegistry;
	localizer: Localizer;
	definitions: UiManagerDefinitions;
	arConfig?: Omit<ARProviderProps, "generatePageUrl">;
	hasEnvironmentScene: boolean;
	initView?: UiView;
	plannerEnabled: boolean;
	configurationMode: ConfigurationMode;
	wallSnapSettings: WallSnapSettings;
	wallVisualizationSettings: WallVisualizationSettings;
	activeWallEditingTool: AnyWallEditingToolType | undefined;
	cameraMode: CameraMode;
	measurementsEnabled: boolean;
	unitSystem: UnitSystem;
	dragOperationActive: boolean;
	configurationHistoryManager: ConfigurationHistoryManager;
	settingsFromQuery: SettingsFromQuery;
	apiDataManager: ApiDataManager;
	onAction: (context: ActionContext) => void;
	onSetScene: (scene: Scene) => void;
	onGenerateCartImage: (image: HTMLImageElement) => void;
	onActive: (node: Record<string, unknown>, active: boolean) => void;
	onViewChanged: (nextView: UiView, previousView?: UiView) => void;
	onRestart: (nextView: UiView, previousView?: UiView) => void;
	onCheckout: () => void;
	onEmitPostMessage: PostMessageHandler["emit"];
	onPickEntities: (
		x: number,
		y: number,
		width?: number,
		height?: number,
	) => Promise<Entity[]>;
	onSelect(...entities: Entity[]): void;
	onDeleteSelected(): boolean;
	canDeleteSelected(): boolean;
	onSetConfigurationMode: (mode: ConfigurationMode) => void;
	onOpenExternalLink: (url: string) => void;
	onCreateRoom: (width: number, depth: number) => boolean;
	onSetWallColor: (name: WallColorName) => void;
	onSetFloorStyle: (name: FloorStyleName) => void;
	onAddDoorToSelectedWall(): boolean;
	onAddWindowToSelectedWall(): boolean;
	onModifyWallSnapSettings: (settings: WallSnapSettings) => void;
	onModifyWallVisualizationSettings: (
		settings: WallVisualizationSettings,
	) => void;
	onSetActiveWallEditingTool: (type: AnyWallEditingToolType) => void;
	getUrl: () => string;
	getPrice: UiBuilderCallbacks["getPrice"];
	onProductSelect(product: unknown): void;
	onSetCameraMode(mode: CameraMode): void;
	onResetCameraFocus(focusOnSelection: boolean): void;
	onToggleMeasurementsEnabled(): void;
	onSetUnitSystem(unitSystem: UnitSystem): void;
	onToggleDragOperationActive(active: boolean): void;
	onUndoCallback: () => void;
	onRedoCallback: () => void;
	onExportToGLTFCallback: () => void;
	getInStockDomainFromColorPropertyCallback: (
		colorProperty: Property,
	) => PropertyValue[];
	getInStockDomainFromProductIdCallback: (
		productId: ProductId,
	) => PropertyValue[];
	getStockHiddenValuesCallback: (
		property: Property,
		fieldValues: unknown[],
	) => unknown[];
	onChangeLocale: (locale: Locale) => void;
};

const initialData = {
	visualization: {
		environmentSceneEnabled: false,
		cameraIsTransitioning: false,
		designModalOpened: false,
		saveModalOpened: false,
	},
};

export type SceneElementInfo = {
	element: ReactElement;
	onRef: (ref: unknown) => void;
};

export class UiManager {
	private _configurator: Configurator;
	private _configuratorBridge: ConfiguratorBridge;
	private _domRoot: string;
	private _theme: Theme;
	private _canvas: HTMLCanvasElement;
	private _metaRegistry: MetaRegistry;
	private _data: UiData;
	private _definitions: UiManagerDefinitions;
	private _views: UiView[];
	private _startView: UiView;
	private _localizer: Localizer;
	private _builder: UiBuilder;
	private _modalManager: ModalManager;
	private _notificationManager: NotificationManager;
	private _settingsFromQuery: SettingsFromQuery;
	private _apiDataManager: ApiDataManager;
	private _sceneObjectMap = new Map<SceneObject, SceneObjectData>();
	private _onActionCallback: UiManagerArgs["onAction"];
	private _onSetSceneCallback: UiManagerArgs["onSetScene"];
	private _onActiveCallback: UiManagerArgs["onActive"];
	private _onViewChangedCallback: UiManagerArgs["onViewChanged"];
	private _onRestartCallback: UiManagerArgs["onRestart"];
	private _onPickEntitiesCallback: UiManagerArgs["onPickEntities"];
	private _onSelectCallback: UiManagerArgs["onSelect"];
	private _onDeleteSelectedCallback: UiManagerArgs["onDeleteSelected"];
	private _canDeleteSelectedCallback: UiManagerArgs["canDeleteSelected"];
	private _onSetConfigurationModeCallback: UiManagerArgs["onSetConfigurationMode"];
	private _onOpenExternalLinkCallback: UiManagerArgs["onOpenExternalLink"];
	private _onCreateRoomCallback: UiManagerArgs["onCreateRoom"];
	private _onSetWallColorCallback: UiManagerArgs["onSetWallColor"];
	private _onSetFloorStyleCallback: UiManagerArgs["onSetFloorStyle"];
	private _onAddDoorToSelectedWallCallback: UiManagerArgs["onAddDoorToSelectedWall"];
	private _onAddWindowToSelectedWallCallback: UiManagerArgs["onAddWindowToSelectedWall"];
	private _onModifyWallSnapSettingsCallback: UiManagerArgs["onModifyWallSnapSettings"];
	private _onModifyWallVisualizationSettingsCallback: UiManagerArgs["onModifyWallVisualizationSettings"];
	private _onSetActiveWallEditingToolCallBack: UiManagerArgs["onSetActiveWallEditingTool"];
	private _getUrlCallback: UiManagerArgs["getUrl"];
	private _onProductSelectCallback: UiManagerArgs["onProductSelect"];
	private _onSetCameraModeCallback: UiManagerArgs["onSetCameraMode"];
	private _onResetCameraFocusCallback: UiManagerArgs["onResetCameraFocus"];
	private _onToggleMeasurementsEnabledCallback: UiManagerArgs["onToggleMeasurementsEnabled"];
	private _onSetUnitSystemCallback: UiManagerArgs["onSetUnitSystem"];
	private _onToggleDragOperationActiveCallback: UiManagerArgs["onToggleDragOperationActive"];
	private _onUndoCallback: () => void;
	private _onRedoCallback: () => void;
	private _onChangeLocaleCallback: UiManagerArgs["onChangeLocale"];
	private _devToolsData: DevToolsData | null = null;

	constructor({
		configurator,
		configuratorBridge,
		domRoot,
		resourceBaseUrl,
		theme,
		canvas,
		metaRegistry,
		localizer,
		definitions,
		arConfig,
		hasEnvironmentScene,
		initView,
		plannerEnabled,
		configurationMode,
		wallSnapSettings,
		wallVisualizationSettings,
		activeWallEditingTool,
		cameraMode,
		measurementsEnabled,
		unitSystem,
		dragOperationActive,
		configurationHistoryManager,
		settingsFromQuery,
		apiDataManager,
		onAction,
		onSetScene,
		onGenerateCartImage,
		onActive,
		onViewChanged,
		onRestart,
		onPickEntities,
		onSelect,
		onDeleteSelected,
		canDeleteSelected,
		onSetConfigurationMode,
		onCreateRoom,
		onSetWallColor,
		onSetFloorStyle,
		onAddDoorToSelectedWall,
		onAddWindowToSelectedWall,
		onModifyWallSnapSettings,
		onModifyWallVisualizationSettings,
		onSetActiveWallEditingTool,
		getUrl,
		getPrice,
		onCheckout,
		onEmitPostMessage,
		onOpenExternalLink,
		onProductSelect,
		onSetCameraMode,
		onResetCameraFocus,
		onToggleMeasurementsEnabled,
		onSetUnitSystem,
		onToggleDragOperationActive,
		onUndoCallback,
		onRedoCallback,
		onExportToGLTFCallback,
		getInStockDomainFromColorPropertyCallback,
		getInStockDomainFromProductIdCallback,
		getStockHiddenValuesCallback,
		onChangeLocale,
	}: UiManagerArgs) {
		this._onActionCallback = onAction;
		this._onSetSceneCallback = onSetScene;
		this._onActiveCallback = onActive;
		this._onViewChangedCallback = onViewChanged;
		this._onRestartCallback = onRestart;
		this._onPickEntitiesCallback = onPickEntities;
		this._onSelectCallback = onSelect;
		this._onDeleteSelectedCallback = onDeleteSelected;
		this._canDeleteSelectedCallback = canDeleteSelected;
		this._onSetConfigurationModeCallback = onSetConfigurationMode;
		this._onOpenExternalLinkCallback = onOpenExternalLink;
		this._onCreateRoomCallback = onCreateRoom;
		this._onSetWallColorCallback = onSetWallColor;
		this._onSetFloorStyleCallback = onSetFloorStyle;
		this._onAddDoorToSelectedWallCallback = onAddDoorToSelectedWall;
		this._onAddWindowToSelectedWallCallback = onAddWindowToSelectedWall;
		this._onModifyWallSnapSettingsCallback = onModifyWallSnapSettings;
		this._onModifyWallVisualizationSettingsCallback =
			onModifyWallVisualizationSettings;
		this._onSetActiveWallEditingToolCallBack = onSetActiveWallEditingTool;
		this._getUrlCallback = getUrl;
		this._onProductSelectCallback = onProductSelect;
		this._onSetCameraModeCallback = onSetCameraMode;
		this._onResetCameraFocusCallback = onResetCameraFocus;
		this._onToggleMeasurementsEnabledCallback = onToggleMeasurementsEnabled;
		this._onSetUnitSystemCallback = onSetUnitSystem;
		this._onToggleDragOperationActiveCallback = onToggleDragOperationActive;
		this._onUndoCallback = onUndoCallback;
		this._onRedoCallback = onRedoCallback;
		this._onChangeLocaleCallback = onChangeLocale;

		this._domRoot = domRoot;
		this._theme = theme;
		this._canvas = canvas;
		this._configurator = configurator;
		this._configuratorBridge = configuratorBridge;
		this._metaRegistry = metaRegistry;
		this._localizer = localizer;
		this._modalManager = new ModalManager();
		this._notificationManager = new NotificationManager();
		this._definitions = definitions;
		this._views = uiViews.filter((view) => definitions.slotNodes[view]);
		this._startView = this._views[0] || "configurator";
		this._settingsFromQuery = settingsFromQuery;
		this._apiDataManager = apiDataManager;
		this._apiDataManager.setOnStateChangeListener(() => {
			this.render();
		});

		const startView = initView ? initView : this._startView;

		const uiNodeQuerier = new UiNodeQuerier(metaRegistry, localizer, getPrice);

		const fieldComponentBuilder = new FieldComponentBuilder({
			getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
			getFieldNodeValueMeta: (...a) =>
				uiNodeQuerier.getFieldNodeValueMeta(...a),
			onActive: this._onActive,
			onValidityChange: this._onFieldValidityChange,
			onShowFieldInfo: this._onShowFieldInfo,
			onShowFieldValueInfo: this._onShowFieldValueInfo,
			onShowFieldValues: this._onShowFieldValues,
		});

		this._builder = new UiBuilderClass(
			configurator,
			resourceBaseUrl,
			metaRegistry,
			localizer,
			definitions.initialActiveProperty,
			{
				onRef: this._onRef,
				onAction: this._onAction,
				onActive: this._onActive,
				onValidityChange: this._onFieldValidityChange,
				onShowFieldInfo: this._onShowFieldInfo,
				onShowFieldValueInfo: this._onShowFieldValueInfo,
				onShowFieldValues: this._onShowFieldValues,
				onNextView: this._onNextView,
				onPreviousView: this._onPreviousView,
				getPrice,
				onProductSelect: this._onProductSelectCallback,
			},
			{
				custom: new CustomComponentBuilderRouter(
					{
						customProductList: new CustomProductListComponentBuilder({
							fieldComponentBuilder,
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
							onProductSelect: this._onProductSelectCallback,
							onProductDrag: (_product, _dragEvent) => {
								// console.log("onProductDrag", product, dragEvent),
							},
						}),
						colorPicker: new ColorPickerComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						colorSelectWithColorPropagation:
							new ColorSelectWithColorPropagationComponentBuilder({
								getFieldNodeMeta: (...a) =>
									uiNodeQuerier.getFieldNodeMeta(...a),
								getFieldNodeValueMeta: (...a) =>
									uiNodeQuerier.getFieldNodeValueMeta(...a),
							}),
						toggleButtonGroup: new ToggleButtonGroupComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						accordion: new AccordionComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						accordionGroup: new AccordionGroupComponentBuilder(),
						decimalInput: new DecimalInputComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						thumbnailDropDown: new ThumbnailDropDownComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						conditionalMessage: new ConditionalMessageComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						horizontalCardSelect: new HorizontalCardSelectComponentBuilder({
							getFieldNodeMeta: (...a) => uiNodeQuerier.getFieldNodeMeta(...a),
							getFieldNodeValueMeta: (...a) =>
								uiNodeQuerier.getFieldNodeValueMeta(...a),
						}),
						visibleGroup: new VisibleGroupComponentBuilder(),
					},
					(node, activeProperty) => {
						const visible = (node as { visible?: unknown }).visible;
						if (visible === undefined) {
							return true;
						}
						if (typeof visible === "boolean") {
							return visible;
						}
						return !!configurator.get(
							resolveProperty(visible as Property, activeProperty),
						).value;
					},
				),
			},
			uiNodeQuerier,
		);

		this._data = {
			visualization: {
				...initialData.visualization,
				designModalOpened: false,
				saveModalOpened: false,
				leftSidebarOpen: true,
				rightSidebarOpen: false, // Grythyttan specific: we don't have a right side bar in this app currently
				multiEditPenPanelOpen: false,
				cameraMode,
				measurementsEnabled,
				unitSystem,
				dragOperationActive,
				pickEntities: this._onPickEntitiesCallback,
				select: this._onSelectCallback,
				deleteSelected: this._onDeleteSelectedCallback,
				canDeleteSelected: this._canDeleteSelectedCallback,
				onCanvasCreated: this._onMainCanvasCreated,
				onToggleEnvironmentScene: hasEnvironmentScene
					? this._onToggleEnvironmentScene
					: undefined,
				onToggleBottombar: this._onToggleBottombar,
				onToggleSidebar: this._onToggleSidebar,
				onToggleMultiEditPenPanel: this._onToggleMultiEditPenPanel,
				onSetCameraMode: this._onSetCameraModeCallback,
				onResetCameraFocus: this._onResetCameraFocusCallback,
				onToggleMeasurements: this._onToggleMeasurementsEnabledCallback,
				onSetUnitSystem: this._onSetUnitSystemCallback,
				onToggleDragOperationActive: this._onToggleDragOperationActiveCallback,
				onToggleOpenDesignModal: this._onToggleDesignModalOpened,
				onToggleSaveModal: this._onToggleSaveModal,
				onOpenLanguageModal: this._onOpenLanguageModal,
			},
			menu: {
				onRestart: this._onRestart,
				onShare: this._onShare,
				onExportToGLTFCallback,
			},
			history: {
				onUndo: this._onUndoCallback,
				onRedo: this._onRedoCallback,
				canUndo: () => configurationHistoryManager.canUndo(),
				canRedo: () => configurationHistoryManager.canRedo(),
			},
			cart: {
				enabled: BuildInfo.mode === "development" || false,
				estimateEnabled: false,
				vatEnabled: true,
				onOpen: this._onOpenCart,
				onCheckout,
				onImageCreated: onGenerateCartImage,
			},
			wall: {
				wallSnapSettings,
				activeWallEditingTool,
				wallVisualizationSettings,
				createRoom: this._onCreateRoomCallback,
				addDoorToSelectedWall: this._onAddDoorToSelectedWallCallback,
				addWindowToSelectedWall: this._onAddWindowToSelectedWallCallback,
				onModifyWallSnapSettings: this._onModifyWallSnapSettingsCallback,
				onSetActiveWallEditingTool: this._onSetActiveWallEditingToolCallBack,
				onModifyWallVisualizationSettings:
					this._onModifyWallVisualizationSettingsCallback,
			},
			room: {
				...defaultRoomAppearance,
				setWallColor: this._onSetWallColorCallback,
				setFloorStyle: this._onSetFloorStyleCallback,
			},
			modals: this._modalManager,
			notifications: this._notificationManager,
			logo: definitions.logo,
			view: startView,
			layout: definitions.layout,
			loading: {
				startup: false,
				application: false,
				scene: false,
			},
			localizer: this._localizer,
			sceneElements: [],
			outOfStockWarningElements: [],
			plannerEnabled,
			configurationMode,
			templatesEnabled: false,
			sceneObjects: [],
			configuratorBridge: this._configuratorBridge,
			uiNodeQuerier,
			emitPostMessage: onEmitPostMessage,
			onSetConfigurationMode: this._onSetConfigurationModeCallback,
			renderUiSlot: this._buildUiSlot,
			onCopy: this._onCopy,
			getInStockDomainFromColorProperty:
				getInStockDomainFromColorPropertyCallback,
			getInStockDomainFromProductId: getInStockDomainFromProductIdCallback,
			getStockHiddenValues: getStockHiddenValuesCallback,
			arConfig,
			getUrl,
		};

		/*
			Prevent pinch-to-zoom on trackpads and ctrl+scroll zooming.
			NOTE: These are not the same as page zooming (ctrl +/- etc).
		*/
		document.addEventListener(
			"wheel",
			(event) => {
				if (event.ctrlKey) {
					event.preventDefault();
				}
			},
			{ passive: false },
		);

		this._onViewChangedCallback(startView);
	}

	public setDevToolsData(data: DevToolsData): void {
		this._devToolsData = data;
	}

	public get builder(): UiBuilder {
		return this._builder;
	}

	public get visualizationData() {
		return this._data.visualization;
	}

	public isSceneObjectInStock(object: SceneObject): boolean {
		if (object.template && object.name === "chair") {
			const visible = this._configurator.get(
				resolveProperty(root.chair.$.visible, object.archetype),
			).value as boolean;
			if (!visible) {
				return true; // invisible slot — no warning
			}
			const chairType = this._configurator.get(
				resolveProperty(root.chair.$.chairType, object.archetype),
			).value as string;
			const chairTheme = this._configurator.get(
				resolveProperty(root.chair.$.chairTheme, object.archetype),
			).value as string;
			const domain = this._data.getInStockDomainFromProductId(
				chairType as ProductId,
			);
			return isProductThemeInStock(chairTheme, domain);
		}

		return true;
	}

	public addOutOfStockWarningElement(
		element: ReactElement,
		onRef: (ref: unknown) => void,
	) {
		this._data.outOfStockWarningElements.push({ element, onRef });
		this.render();
	}

	public removeOutOfStockWarningElement(element: ReactElement) {
		const index = this._data.outOfStockWarningElements.findIndex(
			(info) => info.element === element,
		);
		if (index !== -1) {
			this._data.outOfStockWarningElements.splice(index, 1);
		}
		this.render();
	}

	private _updateSceneObjects = () => {
		const sceneObjects = [];
		for (const [_, sceneObjectData] of this._sceneObjectMap) {
			sceneObjects.push(sceneObjectData);
		}
		this._data.sceneObjects = sceneObjects;
		this.render();
	};

	public addSceneObject = (object: SceneObject) => {
		if (!object.template || object.type !== "sceneObject") {
			return;
		}
		const resolvedProp = resolveProperty(object.template.$, object.archetype);
		const productProperty = this._configurator.get(resolvedProp)
			.property as unknown as Property<Product>;

		const productId = this._configurator.get(productProperty).value.type as
			| ProductId
			| undefined;

		if (!productId) {
			return;
		}

		const sceneObjectData: SceneObjectData = {
			productId,
			colorProperty: productProperty.color,
		};
		this._sceneObjectMap.set(object, sceneObjectData);

		this._updateSceneObjects();
	};

	public removeSceneObject = (object: SceneObject) => {
		const wasDeleted = this._sceneObjectMap.delete(object);

		if (wasDeleted) {
			this._updateSceneObjects();
		}
	};

	public addSceneElement(element: ReactElement, onRef: (ref: unknown) => void) {
		this._data.sceneElements.push({ element, onRef });
		this.render();
	}

	public removeSceneElement(element: ReactElement) {
		const index = this._data.sceneElements.findIndex(
			(info) => info.element === element,
		);
		if (index !== -1) {
			this._data.sceneElements.splice(index, 1);
		}
		this.render();
	}

	public hasSceneElement(element: ReactElement): boolean {
		return this._data.sceneElements.some((info) => info.element === element);
	}

	public toggleStartupLoading(loading: boolean): void {
		this._data.loading.startup = loading;
		this.render();
	}

	public toggleApplicationLoading(loading: boolean): void {
		this._data.loading.application = loading;
		this.render();
	}

	public toggleSceneLoading(loading: boolean): void {
		this._data.loading.scene = loading;
		this.render();
	}

	public setView(view: UiView): void {
		this._data.view = view;
		this.render();
	}

	public setActiveProperty(property: Property) {
		this._builder.setActiveProperty(property);
		this.render();
	}

	public updateConfigurationMode(mode: ConfigurationMode) {
		this._data.configurationMode = mode;
		this.render();
	}

	public updateWallSnapSettings(settings: WallSnapSettings) {
		this._data.wall.wallSnapSettings = settings;
		this.render();
	}

	public updateWallVisualizationSettings(settings: WallVisualizationSettings) {
		this._data.wall.wallVisualizationSettings = settings;
		this.render();
	}

	public updateActiveWallEditingTool(type: AnyWallEditingToolType | undefined) {
		this._data.wall.activeWallEditingTool = type;
		this.render();
	}

	public updateCameraIsTransitioning(transitioning: boolean) {
		this._data.visualization.cameraIsTransitioning = transitioning;
		this._canvas.dataset.cameraTransitioning = String(transitioning);
		this.render();
	}

	public updateRoomAppearance(appearance: RoomAppearance) {
		this._data.room.wallColor = appearance.wallColor;
		this._data.room.floorStyle = appearance.floorStyle;
		this.render();
	}

	public updateCameraMode(mode: CameraMode) {
		this._data.visualization.cameraMode = mode;
		this.render();
	}

	public showFieldChangeNotification = (text: string): void => {
		this._notificationManager.show(
			<Notification icon="notice" color="primary" title={text} />,
		);
	};

	public setMeasurementsEnabled(enabled: boolean) {
		this._data.visualization.measurementsEnabled = enabled;
		this.render();
	}

	public setUnitSystem(unitSystem: UnitSystem) {
		this._data.visualization.unitSystem = unitSystem;
		this.render();
	}

	public toggleDragOperationActive(active: boolean) {
		this._data.visualization.dragOperationActive = active;
		this.render();
	}

	public setCartEnabled(enabled: boolean) {
		this._data.cart.enabled = enabled;
		this.render();
	}
	public setEstimateEnabled(enabled: boolean) {
		this._data.cart.estimateEnabled = enabled;
		this.render();
	}
	public setVATEnabled(enabled: boolean) {
		this._data.cart.vatEnabled = enabled;
		this.render();
	}
	public setTemplatesEnabled(enabled: boolean) {
		this._data.templatesEnabled = enabled;
		this.render();
	}

	public async showARModal(
		props: Pick<
			ARModalProps,
			"onLaunch" | "customErrorMessage" | "errorMessage"
		>,
	): Promise<void> {
		return this._modalManager.show(<ARModal {...props} />).done();
	}

	public render = () => {
		const controllers = [
			new ConfiguratorController(this.render, this._configuratorBridge),
			new ApiDataController(this.render, this._apiDataManager),
		];
		let innerElement = (
			<UiDataProvider data={this._data}>
				<QueryClientProvider client={queryClient}>
					<App theme={this._theme} />
				</QueryClientProvider>
			</UiDataProvider>
		);
		if (this._devToolsData) {
			innerElement = (
				<DevToolsProvider data={this._devToolsData}>
					{innerElement}
				</DevToolsProvider>
			);
		}
		const appRoot: JSX.Element = controllers.reduce(
			(acc: JSX.Element, controller) => controller.createElement(acc),
			innerElement,
		);
		try {
			render(appRoot, document.getElementById(this._domRoot));
		} catch (error) {
			if (error instanceof InvalidTemplatePropertyAccessError) {
				throw new Error(
					`Failed resolving property during render. Please check that you have set a valid active property. Error:\n${error.message}`,
				);
			}
		}
	};

	private _buildUiSlot = (slot: UiSlot): ReactNode => {
		const node = this._definitions.slotNodes[slot];
		return node ? this._builder.buildTree(node) : undefined;
	};

	private _onAction = (node: UiNode, context: ActionContext) => {
		this._onActionCallback(context);
	};

	private _onActive = (node: unknown, active: boolean) => {
		this._onActiveCallback(node as Record<string, unknown>, active);
	};

	private _onFieldValidityChange = (
		_property: Property,
		_validity: InputValidity,
		_value: PropertyValue,
	) => {
		// Not in use
	};

	private _onRef = (elem: HTMLElement, _node: UiNode) => {
		// Replace default canvas created by component builder with
		// canvas used by PlayCanvas
		if (elem instanceof HTMLCanvasElement) {
			this._initRenderCanvas(elem);
		}
	};

	private _onMainCanvasCreated = (canvas: HTMLCanvasElement) => {
		this._initRenderCanvas(canvas);
		this._onSetSceneCallback("main");
	};

	private _initRenderCanvas(canvasToReplace: HTMLCanvasElement) {
		const wrapper = canvasToReplace.parentElement;
		if (wrapper) {
			this._canvas.className = canvasToReplace.className;
			canvasToReplace.remove();
			wrapper.prepend(this._canvas);
		}
	}

	private _onNextView = () => {
		const currentView = this._data.view;
		const currentViewIndex = this._views.indexOf(currentView);
		const nextView = this._views[currentViewIndex + 1];

		if (currentViewIndex !== -1 && nextView !== undefined) {
			this._data.view = nextView;

			this.render();
			this._onViewChangedCallback(this._data.view, currentView);
		}
	};

	private _onPreviousView = () => {
		const currentView = this._data.view;
		const currentViewIndex = this._views.indexOf(currentView);
		const prevView = this._views[currentViewIndex - 1];

		if (currentViewIndex !== -1 && prevView !== undefined) {
			this._data.view = prevView;

			this.render();
			this._onViewChangedCallback(this._data.view, currentView);
		}
	};

	private _onToggleEnvironmentScene = (enabled: boolean) => {
		this._data.visualization.environmentSceneEnabled = enabled;
		this.render();
		this._onSetSceneCallback(enabled ? "environment" : "main");
	};

	private _onToggleBottombar = (open: boolean) => {
		this._data.visualization.leftSidebarOpen = open;
		this.render();
	};

	private _onToggleSidebar = (open: boolean) => {
		this._data.visualization.rightSidebarOpen = open;
		this.render();
	};

	private _onToggleMultiEditPenPanel = (open: boolean) => {
		this._data.visualization.multiEditPenPanelOpen = open;
		this.render();
	};

	private _performRestart = async () => {
		this._onRestartCallback(this._startView, this._data.view);

		this._data.view = this._startView;
		this.render();
	};

	private _onRestart = async () => {
		const handle = await this._modalManager.show(
			<RestartModal
				onConfirm={() => {
					this._performRestart();
					handle.close();
				}}
			/>,
		);
	};

	private _onOpenCart = () => {
		this._modalManager
			.show(<CartModal />, { stackType: screenStackType })
			.done();
	};

	private _onShare = () => {
		this._modalManager.show(<SendEmailModal />).done();
	};

	private _onToggleDesignModalOpened = (opened: boolean) => {
		this._data.visualization.designModalOpened = opened;
		this.render();
	};

	private _onToggleSaveModal = (opened: boolean) => {
		this._data.visualization.saveModalOpened = opened;
		this.render();
	};

	private _onOpenLanguageModal = async () => {
		const newLocale = (await this._modalManager
			.show(<LanguageModal />)
			.done()) as Locale | undefined;

		if (newLocale) {
			this._onChangeLocaleCallback(newLocale);
		}
	};

	private _onShowFieldInfo = (property: Property) => {
		this._modalManager.show(
			<FieldInfoModal
				property={property}
				imageSrc={this._metaRegistry.getPropertyImage(property)}
			/>,
			{ stackType: screenStackType },
		);
	};

	private _onShowFieldValueInfo = (
		property: Property,
		fieldValue: PropertyValue,
		meta: FieldValueMeta,
	) => {
		assertIsLocalizable(property);
		const value = getLocalizablePropertyValue(fieldValue);
		const moreInfoLink = this._data.localizer.localizePropertyValue(
			property,
			value,
			"link",
		);
		const modal = this._modalManager.show(
			<FieldValueInfoModal
				property={property}
				fieldValue={fieldValue}
				fieldValueMeta={{
					...meta,
					image: this._metaRegistry.getPropertyValueImage(property, value),
				}}
				onSelect={() => {
					this._configuratorBridge.setValue(property, fieldValue);
					modal.close();
					if (this._data.view === "productPicker") {
						this._onNextView();
					}
				}}
				onMoreInfo={
					moreInfoLink
						? () => this._onOpenExternalLinkCallback(moreInfoLink)
						: undefined
				}
			/>,
			{ stackType: screenStackType },
		);
	};

	private _onShowFieldValues = (
		property: Property,
		ValuesComponent: FunctionComponent<InputValuesPortalProps>,
	) => {
		this._modalManager.show(
			<FieldValuesModal
				property={property}
				ValuesComponent={ValuesComponent}
			/>,
			{ stackType: screenStackType },
		);
	};

	private _onCopy = (text: string) => {
		const textField = document.createElement("textarea");
		textField.value = text;
		textField.style.position = "fixed";
		textField.style.opacity = "0";
		textField.style.height = "0px";
		textField.style.width = "0px";
		document.body.appendChild(textField);
		textField.select();
		document.execCommand("copy");
		textField.remove();

		const copyText = this._localizer.localizeTerm("copyNotification");
		this._notificationManager.show(
			<Notification icon="copy" color="success" title={copyText} />,
		);
	};
}
